// Use standalone version for Electron compatibility

// Global registry of all active TradingChart instances
// This allows updating ALL instances when price changes, ensuring the visible one always updates
if (!window._tradingChartInstances) {
    window._tradingChartInstances = new Set();
}

export class TradingChart {
    constructor(container, selectedAsset = 'ETH', interval = '15m') {
        // Unique instance ID for debugging
        this._instanceId = Math.random().toString(36).substr(2, 9);
        // console.log('[CHART-INSTANCE] 🆕 NEW TradingChart created, instanceId:', this._instanceId, 'asset:', selectedAsset);
        
        // Register this instance globally
        window._tradingChartInstances.add(this);
        
        this.container = container;
        this.candlestickSeries = null;
        this.selectedAsset = selectedAsset;
        this.interval = interval;
        this.subscription = null;
        
        // Store last positions/orders for recreation after setData() calls
        this.lastPositions = null;
        this.lastOrders = null;
        
        // Track if initial data was loaded successfully
        this.hasInitialData = false;
        
        // Real-time price line tracking
        this.currentPriceLine = null;
        this.lastPrice = 0;
        
        // Track current candle for live price sync with candle bar
        this.lastFormattedCandle = null;
        
        // Spot pair display names mapping - dynamically loaded from spotMeta
        this.spotPairs = {};
        this.spotPairsLoaded = false;

        this.init();
    }

    /**
     * Initialize the chart component
     */
    init() {
        
        if (!this.container) {
            // console.error('❌ Chart container not provided');
            return;
        }

        // Wait for container to be in DOM
        if (!document.contains(this.container)) {
            setTimeout(() => this.init(), 100);
            return;
        }

        // Load spot pairs mapping in background
        this.loadSpotPairs();
        
        // Show loading state
        this.showLoadingState('Initializing chart...');
        
        // Start immediately - no artificial delays
        (async () => {
            try {
                await this.createChart();
                
                // Try to get price from terminal view immediately
                if (window.currentTerminalView?.lastKnownPrice && !this.lastPrice) {
                    this.lastPrice = window.currentTerminalView.lastKnownPrice;
                }
                
                // Load data with timeout protection - don't block indefinitely
                await this.loadInitialData();
                
                // Hide loading state immediately when data ready
                this.hideLoadingState();
            } catch (error) {
                // console.error('Chart initialization error:', error);
                this.hideLoadingState();
            }
        })();
    }

    /**
     * Create the chart instance with Hyperliquid theme
     */
    createChart() {
        
        if (typeof window.LightweightCharts === 'undefined') {
            // console.error('LightweightCharts not loaded');
            return;
        }
        
        this.chart = window.LightweightCharts.createChart(this.container, {
            width: this.container.clientWidth,
            height: this.container.clientHeight,
            layout: {
                background: { color: '#08090A' },
                textColor: '#FFFFFD',
            },
            grid: {
                vertLines: { color: '#313244' },
                horzLines: { color: '#313244' },
            },
            crosshair: {
                mode: 0, // Hidden crosshair mode
                vertLine: {
                    visible: false,
                    labelVisible: false,
                },
                horzLine: {
                    visible: false,
                    labelVisible: false,
                },
            },
            rightPriceScale: {
                borderColor: '#45475a',
                textColor: '#cdd6f4',
            },
            timeScale: {
                borderColor: '#45475a',
                textColor: '#cdd6f4',
                timeVisible: true,
                secondsVisible: false,
            },
        });


        // Create candlestick series
        this.candlestickSeries = this.chart.addCandlestickSeries({
            upColor: '#a6e3a1',
            downColor: '#f38ba8',
            borderDownColor: '#f38ba8',
            borderUpColor: '#a6e3a1',
            wickDownColor: '#f38ba8',
            wickUpColor: '#a6e3a1',
            lastValueVisible: false,  // Hide series' default last value label
            priceLineVisible: false,  // Hide series' default price line
            priceFormat: {
                type: 'custom',
                minMove: 0.000001,
                formatter: (price) => {
                    if (!price || price <= 0) return '0';
                    
                    // Use 5 significant digits (matches orderbook precision)
                    return this.formatToSignificantDigits(price, 5);
                }
            },
        });

        // Candlestick series created
        
        // Apply any pending price update that arrived before chart was ready
        if (this.lastPrice > 0) {
            this.updateCurrentPrice(this.lastPrice);
        }

        // Track position price lines
        this.positionLines = new Map();
        
        // BULLETPROOF FIX: Aggressive periodic check to ensure price line exists
        // This handles race conditions where setData() clears the line
        this.priceLineCheckInterval = setInterval(() => {
            if (this.lastPrice > 0 && this.candlestickSeries && !this.currentPriceLine) {
                // console.log('[CHART] 🔄 Price line missing, recreating at:', this.lastPrice);
                this.updateCurrentPrice(this.lastPrice);
            }
        }, 300); // Check every 300ms for faster recovery

        // Handle resize
        window.addEventListener('resize', this.handleResize.bind(this));
        
    }

    /**
     * Load initial historical data using Charts View advanced data loading
     * REPLACED: Now uses ChartDataManager approach with better caching, validation, and error handling
     */
    async loadInitialData() {
        try {
            if (!this.candlestickSeries) {
                // console.error('❌ Candlestick series not available, cannot load data');
                return;
            }
            
            // Check if browser or Electron mode
            const isElectron = window.process?.versions?.electron;
            
            // OPTIMIZATION: Use pre-warmed data if available (browser mode only)
            if (!isElectron && window.BrowserCandleTracker?.candleData?.length > 0) {
                const tracker = window.BrowserCandleTracker;
                if (tracker.currentAsset === this.selectedAsset && tracker.interval === this.interval) {
                    // Use pre-warmed data instead of fetching
                    const formattedData = this.formatCandleData(tracker.candleData);
                    this.candlestickSeries.setData(formattedData);
                    this.hasInitialData = true;
                    this.fitContent();
                    
                    // Apply price line if available
                    if (this.lastPrice > 0) {
                        this.updateCurrentPrice(this.lastPrice);
                    } else if (window.currentTerminalView?.lastKnownPrice) {
                        this.lastPrice = window.currentTerminalView.lastKnownPrice;
                        this.updateCurrentPrice(this.lastPrice);
                    }
                    
                    return; // Skip HTTP fetch - data already loaded
                }
            }
            
            // Calculate time range based on interval for appropriate historical coverage
            const now = Date.now();
            let historicalDays;
            switch (this.interval) {
                case '1m':
                    historicalDays = 3; // 3 days for 1-minute
                    break;
                case '5m':
                    historicalDays = 7; // 1 week for 5-minute
                    break;
                case '15m':
                    historicalDays = 1; // 24 hours for 15-minute
                    break;
                case '1h':
                    historicalDays = 60; // 2 months for 1-hour
                    break;
                case '4h':
                    historicalDays = 180; // 6 months for 4-hour
                    break;
                case '1d':
                    historicalDays = 365; // 1 year for daily
                    break;
                default:
                    historicalDays = 30; // Default 30 days
            }
            
            const startTime = now - (historicalDays * 24 * 60 * 60 * 1000);
            const endTime = now;
            
            // console.log(`📊 Loading ${historicalDays} days of ${this.selectedAsset} ${this.interval} data...`);
            
            // Request data from Hyperliquid API with detailed params logging
            const requestParams = {
                asset: this.selectedAsset,
                interval: this.interval,
                startTime: startTime,
                endTime: endTime,
                limit: 5000
            };
            
            let response;
            
            if (!isElectron) {
                // Browser mode - use BrowserHyperliquidClient
                if (!window.BrowserHyperliquidClient?.isInitialized) {
                    // console.error('❌ BrowserHyperliquidClient not initialized');
                    return;
                }
                
                const candleData = await window.BrowserHyperliquidClient.getCandleData(
                    this.selectedAsset,
                    this.interval,
                    startTime,
                    endTime
                );
                response = { success: true, data: candleData };
            } else {
                // Electron mode - use IPC
                response = await window.electronAPI.invoke('hyperliquid:getCandleData', requestParams);
            }
            
            if (response.success && Array.isArray(response.data)) {
                // Validate and clean the data before setting (Charts View pattern)
                const validData = response.data.filter(candle => {
                    if (!candle || typeof candle !== 'object') return false;
                    
                    const time = candle.t || candle.time;
                    const open = parseFloat(candle.o || candle.open);
                    const high = parseFloat(candle.h || candle.high);
                    const low = parseFloat(candle.l || candle.low);
                    const close = parseFloat(candle.c || candle.close);
                    const volume = parseFloat(candle.v || candle.volume || 0);
                    
                    return time && !isNaN(open) && !isNaN(high) && !isNaN(low) && !isNaN(close) && volume >= 0;
                });
                
                // console.log(`📊 Loaded ${validData.length}/${response.data.length} valid candles for ${this.selectedAsset} ${this.interval}`);
                
                // Format and set data
                const formattedData = this.formatCandleData(validData);
                // console.log('[CHART-DATA] setData called on instance:', this._instanceId, 'currentPriceLine before:', !!this.currentPriceLine);
                this.candlestickSeries.setData(formattedData);
                // console.log('[CHART-DATA] setData complete on instance:', this._instanceId, 'currentPriceLine after:', !!this.currentPriceLine);
                
                // Mark that we have initial data
                this.hasInitialData = true;
                
                // Fit content to show all data
                this.fitContent();
                
                // CRITICAL: Recreate price line immediately after setData() clears it
                // console.log('[CHART-DATA] About to recreate price line, lastPrice:', this.lastPrice, 'terminalLastKnownPrice:', window.currentTerminalView?.lastKnownPrice);
                if (this.lastPrice > 0) {
                    this.updateCurrentPrice(this.lastPrice);
                } else if (window.currentTerminalView?.lastKnownPrice) {
                    // Fallback: Use terminal view's last known price if chart hasn't received price yet
                    this.lastPrice = window.currentTerminalView.lastKnownPrice;
                    this.updateCurrentPrice(this.lastPrice);
                }
                
                // BULLETPROOF: Multiple aggressive retries to ensure price line appears
                const retryDelays = [50, 150, 300, 600, 1000];
                retryDelays.forEach(delay => {
                    setTimeout(() => {
                        if (this.candlestickSeries && !this.currentPriceLine) {
                            // Try to get price from multiple sources
                            const price = this.lastPrice || window.currentTerminalView?.lastKnownPrice;
                            if (price > 0) {
                                this.lastPrice = price;
                                this.updateCurrentPrice(price);
                            }
                        }
                    }, delay);
                });
            } else {
                // console.error(`❌ Failed to load initial chart data:`, {
                //     success: response.success,
                //     error: response.error,
                //     hasData: Array.isArray(response.data),
                //     dataLength: response.data?.length
                // });
            }
        } catch (error) {
            // console.error('❌ Error loading initial chart data:', error);
        }
    }


    /**
     * Format number to significant digits (for price precision)
     * Matches orderbook precision handling
     */
    formatToSignificantDigits(num, sigDigits = 5) {
        if (num === 0 || !num) return '0';
        
        const absNum = Math.abs(num);
        
        // BTC exception: if price >= 10000, show whole numbers only (no decimals)
        if (absNum >= 10000) {
            return Math.round(num).toString();
        }
        
        // For other assets: use significant digits
        const magnitude = Math.floor(Math.log10(absNum));
        const precision = sigDigits - magnitude - 1;
        
        if (precision < 0) {
            // Instead of scientific notation, show with 2 decimals
            return num.toFixed(2);
        } else {
            return num.toFixed(Math.max(0, precision));
        }
    }

    /**
     * Format candle data for Lightweight Charts
     */
    formatCandleData(candles) {
        if (!Array.isArray(candles)) return [];

        return candles.map(candle => this.formatSingleCandle(candle)).filter(Boolean);
    }

    /**
     * Format single candle data
     */
    formatSingleCandle(candle) {
        if (!candle || typeof candle !== 'object') {
            return null;
        }

        // Hyperliquid SDK format: { t, T, s, i, o, c, h, l, v, n }
        const time = candle.t; // Opening timestamp in ms
        const open = parseFloat(candle.o);
        const high = parseFloat(candle.h);
        const low = parseFloat(candle.l);
        const close = parseFloat(candle.c);

        if (!time || isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) {
            return null;
        }

        return {
            time: Math.floor(time / 1000), // Convert ms to seconds for lightweight-charts
            open,
            high,
            low,
            close
        };
    }

    /**
     * Change the selected asset - DEPRECATED
     * Terminal-view handles this via child process lifecycle (stopCandleChart + startCandleChart)
     * This method should NOT be used anymore - keeping for backwards compatibility
     */
    async changeAsset(newAsset) {
        // console.warn('⚠️ TradingChart.changeAsset() is deprecated - terminal-view should manage child process lifecycle');
        
        if (newAsset === this.selectedAsset) {
            return;
        }

        // Just update the label for UI synchronization
        this.selectedAsset = newAsset;
        
        // Clear existing data and overlays
        if (this.candlestickSeries) {
            this.candlestickSeries.setData([]);
        }
        this.clearPositionOverlaysSync();
        this.lastFormattedCandle = null;  // Reset candle tracking
    }

    /**
     * Change chart interval
     * REPLACED: Uses Charts View advanced loading with better error handling
     */
    async changeInterval(newInterval) {
        if (newInterval === this.interval) return;

        // console.log(`📊 Changing interval from ${this.interval} to ${newInterval}`);
        
        // Show loading state
        this.showLoadingState(`Loading ${newInterval} data...`);
        this.interval = newInterval;

        // Clear existing data and reset flag
        if (this.candlestickSeries) {
            this.candlestickSeries.setData([]);
        }
        this.hasInitialData = false;  // Reset flag for new interval
        this.lastFormattedCandle = null;  // Reset candle tracking

        // Reload with new interval using advanced loading
        await this.loadInitialData();
        
        // Note: Terminal view handles live updates via candle-live.js child process
        // The child process continues streaming at the new interval
        
        // Hide loading state immediately when data ready
        this.hideLoadingState();
    }

    /**
     * Show loading state with overlay
     */
    showLoadingState(message = 'Loading...') {
        // Remove existing overlay
        this.hideLoadingState();
        
        // Add loading class to container for background effects
        this.container.classList.add('loading');
        
        // Create loading overlay
        const overlay = document.createElement('div');
        overlay.className = 'chart-loading-overlay';
        overlay.innerHTML = `
            <div class="chart-loading-content">
                <div class="chart-loading-spinner"></div>
                <div class="chart-loading-text">${message}</div>
            </div>
        `;
        
        // Add overlay to container
        this.container.style.position = 'relative';
        this.container.appendChild(overlay);
        
        // Smooth fade in with staggered animation
        requestAnimationFrame(() => {
            overlay.style.opacity = '1';
            overlay.style.transform = 'scale(1)';
        });
    }
    
    /**
     * Hide loading state with smooth transition
     * SSEv4-main pattern: No minimum duration - hide immediately
     */
    hideLoadingState() {
        // Remove loading class from container
        this.container.classList.remove('loading');
        
        const overlay = this.container.querySelector('.chart-loading-overlay');
        if (overlay && overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
        }
    }

    /**
     * Unsubscribe from real-time updates
     */
    async unsubscribeFromUpdates() {
        if (this.subscription) {
            // Remove event listeners
            window.electronAPI.removeAllListeners('hyperliquid:candleUpdate');
            this.subscription = null;
        }
    }

    /**
     * Handle window resize
     */
    handleResize() {
        if (this.chart && this.container) {
            this.chart.applyOptions({
                width: this.container.clientWidth,
            });
        }
    }

    /**
     * Fit chart content to visible range
     */
    fitContent() {
        if (this.chart) {
            this.chart.timeScale().fitContent();
        }
    }

    /**
     * Clear position overlays synchronously
     * SSEv4-main pattern: Simple, blocking clear for predictability
     */
    clearPositionOverlaysSync() {
        if (!this.positionLines || this.positionLines.size === 0) {
            return;
        }
        
        // Remove all lines synchronously
        this.positionLines.forEach((line, key) => {
            if (this.candlestickSeries) {
                try {
                    this.candlestickSeries.removePriceLine(line);
                } catch (error) {
                    // Silently handle errors (line may already be removed)
                }
            }
        });
        
        // Clear the map
        this.positionLines.clear();
    }

    /**
     * Clear position overlays synchronously
     * FIX: Async clearing caused timing issues with rapid recreations
     */
    clearPositionOverlaysAsync() {
        if (!this.positionLines || this.positionLines.size === 0) {
            return;
        }
        
        // Remove all lines synchronously to avoid race conditions
        this.positionLines.forEach((line, key) => {
            if (this.candlestickSeries) {
                try {
                    this.candlestickSeries.removePriceLine(line);
                } catch (error) {
                    // Silently handle errors (line may already be removed)
                }
            }
        });
        
        // Clear the map
        this.positionLines.clear();
    }

    /**
     * Update position and order overlays on chart
     */
    updatePositionOverlays(positions, orders) {
        if (!this.candlestickSeries) return;

        // Store positions/orders for recreation after setData() calls
        this.lastPositions = positions;
        this.lastOrders = orders;

        // OPTIMIZATION: Clear existing position lines asynchronously
        this.clearPositionOverlaysAsync();

        // Add position entry lines
        if (positions) {
            const matchingPositions = positions.filter(pos => pos.coin === this.selectedAsset);
            matchingPositions.forEach(position => {
                const isLong = position.side === 'LONG';
                const entryPrice = parseFloat(position.entryPrice);
                const color = isLong ? '#3b82f6' : '#ef4444'; // Blue for longs, red for shorts

                // For spot pairs, just show pair name. For perps, show side + asset
                const isSpot = position.coin && position.coin.startsWith('@');
                const title = isSpot 
                    ? this.getAssetDisplayName(position.coin)
                    : `${position.side} ${this.getAssetDisplayName(position.coin)}`;
                
                const priceLineOptions = {
                    price: entryPrice,
                    color: color,
                    lineWidth: 2,
                    lineStyle: 1, // Solid line
                    axisLabelVisible: true,
                    title: title
                };

                const priceLine = this.candlestickSeries.createPriceLine(priceLineOptions);
                this.positionLines.set(`pos_${position.coin}`, priceLine);
            });
        }

        // Add open order lines
        if (orders) {
            // Filter for non-TP/SL orders matching current asset
            const matchingOrders = orders.filter(order => {
                const matchesAsset = order.coin === this.selectedAsset;
                const isNotTpsl = !order.isPositionTpsl; // FIX: Truthy check, not strict equality
                // Asset and order matching logic
                return matchesAsset && isNotTpsl;
            });
            
            matchingOrders.forEach(order => {
                const orderPrice = parseFloat(order.limitPx);
                const color = order.side === 'BUY' ? '#3b82f6' : '#ef4444';

                // For spot pairs, just show pair name. For perps, show side + asset
                const isSpot = order.coin && order.coin.startsWith('@');
                const title = isSpot 
                    ? this.getAssetDisplayName(order.coin)
                    : `${order.side} ${this.getAssetDisplayName(order.coin)}`;
                
                const priceLineOptions = {
                    price: orderPrice,
                    color: color,
                    lineWidth: 1.5,
                    lineStyle: 2, // Dashed line
                    axisLabelVisible: true,
                    title: title
                };

                try {
                    const priceLine = this.candlestickSeries.createPriceLine(priceLineOptions);
                    this.positionLines.set(`order_${order.oid}`, priceLine);
                } catch (error) {
                    // Silently handle errors for invalid price lines
                    // console.error('Error creating order price line:', error);
                }
            });

            // Add TP/SL order lines
            const tpslOrders = orders.filter(order => order.coin === this.selectedAsset && order.isPositionTpsl);
            tpslOrders.forEach(order => {
                const orderPrice = parseFloat(order.limitPx);
                
                // Determine if this is a profitable exit (TP) or stop loss
                const matchingPosition = positions?.find(p => p.coin === order.coin);
                if (matchingPosition) {
                    const isLong = matchingPosition.side === 'LONG';
                    const isProfitable = isLong ? 
                        (orderPrice > matchingPosition.entryPrice) : 
                        (orderPrice < matchingPosition.entryPrice);

                    // For spot pairs, just show pair name. For perps, show TP/SL + asset
                    const isSpot = order.coin && order.coin.startsWith('@');
                    const title = isSpot 
                        ? this.getAssetDisplayName(order.coin)
                        : `${isProfitable ? 'TP' : 'SL'} ${this.getAssetDisplayName(order.coin)}`;
                    
                    const priceLineOptions = {
                        price: orderPrice,
                        color: isProfitable ? '#4ade80' : '#f87171',
                        lineWidth: 1.5,
                        lineStyle: 3, // Dotted line
                        axisLabelVisible: true,
                        title: title
                    };

                    const priceLine = this.candlestickSeries.createPriceLine(priceLineOptions);
                    this.positionLines.set(`tpsl_${order.oid}`, priceLine);
                }
            });
        }
    }

    /**
     * Destroy the chart and clean up resources
     */
    destroy() {
        // console.log('[CHART-INSTANCE] 🗑️ DESTROY called on instance:', this._instanceId);
        
        // Remove from global registry
        window._tradingChartInstances?.delete(this);
        
        this.unsubscribeFromUpdates();
        this.hideLoadingState();
        
        // Clear price line check interval
        if (this.priceLineCheckInterval) {
            clearInterval(this.priceLineCheckInterval);
            this.priceLineCheckInterval = null;
        }
        
        // Remove current price line
        if (this.currentPriceLine && this.candlestickSeries) {
            // console.log('[CHART-INSTANCE] 🗑️ Removing price line on instance:', this._instanceId);
            try {
                this.candlestickSeries.removePriceLine(this.currentPriceLine);
            } catch (e) {
                // Silently handle if already removed
            }
            this.currentPriceLine = null;
        }
        
        // OPTIMIZATION: Clear position lines efficiently
        if (this.positionLines && this.positionLines.size > 0) {
            const linesToRemove = Array.from(this.positionLines.values());
            this.positionLines.clear();
            
            if (this.candlestickSeries) {
                for (const line of linesToRemove) {
                    try {
                        this.candlestickSeries.removePriceLine(line);
                    } catch (error) {
                        // Silently handle errors during cleanup
                    }
                }
            }
        }
        
        if (this.chart) {
            window.removeEventListener('resize', this.handleResize.bind(this));
            this.chart.remove();
            this.chart = null;
        }
        
        this.candlestickSeries = null;
        this.lastPrice = 0;
        this.lastFormattedCandle = null;  // Reset candle tracking
    }

    /**
     * Get current chart data
     */
    getData() {
        if (this.candlestickSeries) {
            return this.candlestickSeries.data();
        }
        return [];
    }

    /**
     * Update chart with new candle data from live stream
     * OPTION C: Smart transition - only setData() if no initial data exists
     */
    updateCandles(historicalData, currentCandle) {
        try {
            if (!this.candlestickSeries) {
                // console.warn('❌ No candlestick series available for update');
                return;
            }

            // If we have historical data from child process
            if (historicalData && Array.isArray(historicalData) && historicalData.length > 0) {
                // OPTION C: Only use setData() if we DON'T have initial data yet
                if (!this.hasInitialData) {
                    // console.log('[CHART-CANDLES] updateCandles setData on instance:', this._instanceId, 'priceLine before:', !!this.currentPriceLine);
                    const formattedHistorical = this.formatCandleData(historicalData);
                    
                    // setData() clears all price lines - we need to recreate them after
                    this.candlestickSeries.setData(formattedHistorical);
                    // console.log('[CHART-CANDLES] updateCandles setData complete, priceLine after:', !!this.currentPriceLine);
                    this.hasInitialData = true;
                    
                    // Ensure the view shows sufficient history after a bulk update
                    this.fitContent();
                    
                    // FIX: Single delayed overlay recreation
                    if (this.lastPositions || this.lastOrders) {
                        setTimeout(() => {
                            if (this.lastPositions || this.lastOrders) {
                                this.updatePositionOverlays(this.lastPositions, this.lastOrders);
                            }
                        }, 150);
                    }
                    
                    // Recreate price line immediately after setData() clears it
                    const price = this.lastPrice || window.currentTerminalView?.lastKnownPrice;
                    if (price > 0) {
                        this.lastPrice = price;
                        this.updateCurrentPrice(price);
                    }
                    
                    // BULLETPROOF: Aggressive retries for live candle updates
                    const retryDelays = [50, 150, 300];
                    retryDelays.forEach(delay => {
                        setTimeout(() => {
                            if (this.candlestickSeries && !this.currentPriceLine) {
                                const retryPrice = this.lastPrice || window.currentTerminalView?.lastKnownPrice;
                                if (retryPrice > 0) {
                                    this.lastPrice = retryPrice;
                                    this.updateCurrentPrice(retryPrice);
                                }
                            }
                        }, delay);
                    });
                } else {
                    // We already have initial data - just update the current candle
                    // console.log('📊 Initial data exists - skipping historicalData setData()');
                }
            }

            // Update with current live candle (incremental update, doesn't clear overlays)
            if (currentCandle) {
                const formattedCandle = this.formatSingleCandle(currentCandle);
                if (formattedCandle) {
                    // console.log('📊 [CHART] Updating candle:', {
                    //     time: new Date(formattedCandle.time * 1000).toISOString(),
                    //     close: formattedCandle.close
                    // });
                    this.candlestickSeries.update(formattedCandle);
                    // Store for live price sync
                    this.lastFormattedCandle = { ...formattedCandle };
                }
            }

        } catch (error) {
            // console.error('❌ Error updating candles:', error);
        }
    }
    
    /**
     * Update current market price line on chart
     * Called from orderbook updates for real-time price display
     * BULLETPROOF VERSION: Handles all edge cases and race conditions
     */
    updateCurrentPrice(price) {
        // console.log('[CHART-PRICE] ========== updateCurrentPrice START ==========');
        // console.log('[CHART-PRICE] Instance ID:', this._instanceId);
        // console.log('[CHART-PRICE] Input price:', price);
        // console.log('[CHART-PRICE] this.lastPrice (before):', this.lastPrice);
        // console.log('[CHART-PRICE] this.candlestickSeries exists:', !!this.candlestickSeries);
        // console.log('[CHART-PRICE] this.currentPriceLine exists:', !!this.currentPriceLine);
        // console.log('[CHART-PRICE] this.currentPriceLine object:', this.currentPriceLine);
        
        if (!price || price <= 0) {
            // console.log('[CHART-PRICE] ❌ RETURNING: Invalid price');
            return;
        }
        
        // Store price immediately for later use
        this.lastPrice = price;
        // console.log('[CHART-PRICE] this.lastPrice (after):', this.lastPrice);
        
        // If chart not ready yet, return (price is stored for later)
        if (!this.candlestickSeries) {
            // console.log('[CHART-PRICE] ❌ RETURNING: No candlestickSeries');
            return;
        }
        
        try {
            // If price line exists, UPDATE it with applyOptions (more reliable than remove/create)
            if (this.currentPriceLine) {
                // console.log('[CHART-PRICE] 🔄 Attempting applyOptions with price:', price);
                // console.log('[CHART-PRICE] Price line options() before:', this.currentPriceLine.options ? this.currentPriceLine.options() : 'no options method');
                this.currentPriceLine.applyOptions({ price: price });
                // console.log('[CHART-PRICE] ✅ applyOptions called successfully');
                // console.log('[CHART-PRICE] Price line options() after:', this.currentPriceLine.options ? this.currentPriceLine.options() : 'no options method');
                
                // Also update current candle bar to sync with price line
                this.updateCurrentCandleWithPrice(price);
                
                // console.log('[CHART-PRICE] ========== updateCurrentPrice END (applyOptions) ==========');
                return;
            }
            
            // console.log('[CHART-PRICE] 🆕 Creating NEW price line at:', price);
            // Create new price line at current market price
            this.currentPriceLine = this.candlestickSeries.createPriceLine({
                price: price,
                color: '#00BF63',           // EVPlus green
                lineWidth: 2,
                lineStyle: 2,               // Dashed (LineStyle.Dashed = 2)
                axisLabelVisible: true,
                title: '',                  // No title - only show price on axis
                lineVisible: true
            });
            // console.log('[CHART-PRICE] ✅ Price line created:', this.currentPriceLine);
            
            // Also update current candle bar to sync with price line
            this.updateCurrentCandleWithPrice(price);
            
            // console.log('[CHART-PRICE] ========== updateCurrentPrice END (created) ==========');
            
        } catch (error) {
            // console.error('[CHART-PRICE] ❌ ERROR:', error);
            // Reset and retry
            this.currentPriceLine = null;
            setTimeout(() => {
                if (this.candlestickSeries && !this.currentPriceLine && this.lastPrice > 0) {
                    try {
                        this.currentPriceLine = this.candlestickSeries.createPriceLine({
                            price: this.lastPrice,
                            color: '#00BF63',
                            lineWidth: 2,
                            lineStyle: 2,
                            axisLabelVisible: true,
                            title: '',
                            lineVisible: true
                        });
                        // console.log('[CHART-PRICE] ✅ Retry price line created');
                    } catch (retryError) {
                        // console.error('[CHART-PRICE] ❌ Retry also failed:', retryError);
                    }
                }
            }, 100);
        }
    }

    /**
     * Update asset label (for UI synchronization)
     */
    updateAssetLabel(asset) {
        this.selectedAsset = asset;
    }
    
    /**
     * Update current candle bar with live price
     * Syncs the current candle's close/high/low with the price line
     */
    updateCurrentCandleWithPrice(price) {
        if (!this.lastFormattedCandle || !this.candlestickSeries || !price || price <= 0) {
            return;
        }
        
        try {
            // Update candle with new price - expand high/low if needed
            const updatedCandle = {
                time: this.lastFormattedCandle.time,
                open: this.lastFormattedCandle.open,
                high: Math.max(this.lastFormattedCandle.high, price),
                low: Math.min(this.lastFormattedCandle.low, price),
                close: price
            };
            
            // Apply update to chart
            this.candlestickSeries.update(updatedCandle);
            
            // Update stored candle with new values
            this.lastFormattedCandle.high = updatedCandle.high;
            this.lastFormattedCandle.low = updatedCandle.low;
            this.lastFormattedCandle.close = price;
        } catch (error) {
            // Silently handle - candle sync is non-critical
        }
    }
    
    /**
     * Static method to update price on ALL chart instances
     * This ensures the visible chart always gets updated regardless of which instance is tracked
     */
    static updateAllInstancesPrice(price, asset) {
        if (!window._tradingChartInstances || !price || price <= 0) return;
        
        for (const chart of window._tradingChartInstances) {
            // Only update charts for the matching asset
            if (chart.selectedAsset === asset && chart.candlestickSeries) {
                chart.updateCurrentPrice(price);
            }
        }
    }
    
    /**
     * Load spot pairs mapping from spotMeta
     * Dynamically fetches ALL spot pairs, not just hardcoded ones
     */
    async loadSpotPairs() {
        if (this.spotPairsLoaded) return;
        
        try {
            if (!window.BrowserHyperliquidClient?.isInitialized) {
                // Client not ready yet, retry later
                setTimeout(() => this.loadSpotPairs(), 1000);
                return;
            }
            
            const spotMeta = await window.BrowserHyperliquidClient.getSpotMeta();
            if (spotMeta?.universe && spotMeta?.tokens) {
                // Build mapping: @index → { name, baseToken, quoteToken }
                for (const pair of spotMeta.universe) {
                    const baseToken = spotMeta.tokens.find(t => t.index === pair.tokens[0]);
                    const quoteToken = spotMeta.tokens.find(t => t.index === pair.tokens[1]);
                    
                    if (baseToken && quoteToken) {
                        this.spotPairs[`@${pair.index}`] = {
                            name: pair.name || `${baseToken.name}/${quoteToken.name}`,
                            baseToken: baseToken.name,
                            quoteToken: quoteToken.name
                        };
                    }
                }
                this.spotPairsLoaded = true;
                // console.log(`[CHART] Loaded ${Object.keys(this.spotPairs).length} spot pairs`);
            }
        } catch (error) {
            // Silently fail - will retry or use fallback
            // console.warn('[CHART] Failed to load spot pairs:', error.message);
        }
    }
    
    /**
     * Get display name for asset (spot pairs show as USDH/USDC, others show as-is)
     * Matches terminal-view.js formatAssetWithDexBadge() pattern
     */
    getAssetDisplayName(asset) {
        if (asset && asset.startsWith('@')) {
            // Check dynamic spot pairs first
            if (this.spotPairs[asset]) {
                return this.spotPairs[asset].name;
            }
            // Fallback: return as-is if not found
            return asset;
        }
        return asset;
    }

    /**
     * Check if chart is ready
     */
    isReady() {
        return this.chart !== null && this.candlestickSeries !== null;
    }
}

// Export for global access
window.TradingChart = TradingChart;
